import { useId, type ReactElement } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '@csa/ui';

/**
 * Editing headers and query parameters.
 *
 * Rows carry an `enabled` flag rather than being removed, because the usual
 * debugging motion is to turn one header off, see what changes, and turn it
 * back on. Deleting and retyping loses the value.
 */

export interface KeyValueRow {
  readonly id: string;
  readonly key: string;
  readonly value: string;
  readonly enabled: boolean;
}

export function newRow(): KeyValueRow {
  return { id: crypto.randomUUID(), key: '', value: '', enabled: true };
}

export function toRecord(rows: readonly KeyValueRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    if (!row.enabled) continue;
    const key = row.key.trim();
    if (key.length === 0) continue;
    out[key] = row.value;
  }
  return out;
}

const INPUT =
  'w-full rounded-md border border-line bg-surface px-2 py-1.5 font-mono text-xs text-body ' +
  'placeholder:text-subtle focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40';

export function KeyValueEditor({
  rows,
  onChange,
  keyLabel,
  reservedKeys = [],
}: {
  rows: readonly KeyValueRow[];
  onChange: (rows: KeyValueRow[]) => void;
  keyLabel: string;
  reservedKeys?: readonly string[];
}): ReactElement {
  const groupId = useId();

  const update = (id: string, patch: Partial<KeyValueRow>): void => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  return (
    <div className="flex flex-col gap-1.5">
      {rows.length > 0 ? (
        <div
          className="grid grid-cols-[1.25rem_1fr_1.5fr_2.25rem] gap-2 px-1 text-xs font-medium text-subtle"
          aria-hidden
        >
          <span />
          <span>{keyLabel}</span>
          <span>Value</span>
          <span />
        </div>
      ) : null}

      {rows.map((row, index) => {
        const reserved = reservedKeys.includes(row.key.trim().toLowerCase());
        const describedBy = reserved ? `${groupId}-${row.id}-reserved` : undefined;

        return (
          <div
            key={row.id}
            className="grid grid-cols-[1.25rem_1fr_1.5fr_2.25rem] items-center gap-2"
          >
            <input
              type="checkbox"
              className="size-4 accent-brand"
              checked={row.enabled}
              onChange={(event) => update(row.id, { enabled: event.target.checked })}
              aria-label={`Include ${row.key || `row ${index + 1}`}`}
            />

            <input
              className={INPUT}
              value={row.key}
              onChange={(event) => update(row.id, { key: event.target.value })}
              placeholder={keyLabel.toLowerCase()}
              spellCheck={false}
              aria-label={`${keyLabel} name`}
              aria-invalid={reserved || undefined}
              aria-describedby={describedBy}
            />

            <input
              className={INPUT}
              value={row.value}
              onChange={(event) => update(row.id, { value: event.target.value })}
              placeholder="value"
              spellCheck={false}
              aria-label={`${keyLabel} value`}
            />

            <Button
              variant="ghost"
              size="xs"
              iconOnly
              aria-label={`Remove ${row.key || `row ${index + 1}`}`}
              onClick={() => onChange(rows.filter((other) => other.id !== row.id))}
            >
              <TrashIcon className="size-4" aria-hidden />
            </Button>

            {reserved ? (
              <p className="col-span-4 px-1 text-xs text-warning-fg" id={describedBy}>
                <code className="font-mono">{row.key.trim()}</code> is set by the tool and will be
                refused. Use a managed credential instead of setting it by hand.
              </p>
            ) : null}
          </div>
        );
      })}

      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        leadingIcon={<PlusIcon className="size-4" aria-hidden />}
        onClick={() => onChange([...rows, newRow()])}
      >
        Add {keyLabel.toLowerCase()}
      </Button>
    </div>
  );
}
